"use client"

import { useEffect } from "react"
import Link from "next/link"
import { trackEvent } from "@/lib/analytics"
import { Check, X, ArrowRight, Crown } from "lucide-react"
import { Button } from "@/components/ui/button"

type CellValue = true | false | string

interface ComparisonRow {
  feature: string
  transcriptr: CellValue
  opusclip: CellValue
  vizard: CellValue
}

function CellContent({ value, highlight = false }: { value: CellValue; highlight?: boolean }) {
  if (value === true) return <Check className={`w-5 h-5 mx-auto ${highlight ? "text-emerald-400" : "text-emerald-400/60"}`} />
  if (value === false) return <X className="w-5 h-5 text-zinc-600 mx-auto" />
  return <span className={`text-sm ${highlight ? "text-amber-400" : "text-white/60"}`}>{value}</span>
}

const ROWS: ComparisonRow[] = [
  { feature: "AI Clip Detection", transcriptr: true, opusclip: true, vizard: true },
  { feature: "Virality Scoring (0-100)", transcriptr: true, opusclip: true, vizard: false },
  { feature: "Free Tier Available", transcriptr: true, opusclip: "Limited", vizard: "Limited" },
  { feature: "51+ AI Content Tools", transcriptr: true, opusclip: false, vizard: false },
  { feature: "Workflow Automation", transcriptr: true, opusclip: "Limited", vizard: false },
  { feature: "Multi-Track Timeline Editor", transcriptr: true, opusclip: "Limited", vizard: "Basic" },
  { feature: "B-Roll Generation (AI + Stock)", transcriptr: true, opusclip: true, vizard: false },
  { feature: "Background Music (10K+ tracks)", transcriptr: true, opusclip: true, vizard: "Limited" },
  { feature: "Animated Captions (48+ languages)", transcriptr: true, opusclip: true, vizard: true },
  { feature: "Auto-Reframe with Face Detection", transcriptr: true, opusclip: true, vizard: true },
  { feature: "AI Voiceover (ElevenLabs)", transcriptr: true, opusclip: false, vizard: false },
  { feature: "AI Thumbnail Generation", transcriptr: true, opusclip: "Basic", vizard: false },
  { feature: "Emoji Overlays", transcriptr: true, opusclip: false, vizard: false },
  { feature: "Multi-Platform Publishing", transcriptr: true, opusclip: true, vizard: true },
  { feature: "Publishing Calendar", transcriptr: true, opusclip: false, vizard: false },
  { feature: "Study Notes & Flashcards", transcriptr: true, opusclip: false, vizard: false },
  { feature: "Blog Post Generation", transcriptr: true, opusclip: false, vizard: false },
  { feature: "Channel Monitoring", transcriptr: true, opusclip: false, vizard: false },
]

export function ThreeWayComparison() {
  useEffect(() => {
    trackEvent("comparison_page_viewed", { competitor: "OpusClip vs Vizard (three-way)" })
  }, [])

  const transcriptrWins = ROWS.filter(r => r.transcriptr === true && (r.opusclip !== true || r.vizard !== true)).length

  return (
    <main className="pt-24 pb-16">
      <div className="container mx-auto px-5 max-w-5xl">
        {/* Hero */}
        <div className="text-center space-y-4 mb-16">
          <p className="text-sm text-[#ff6b4a] font-semibold uppercase tracking-wider">Three-Way Comparison</p>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white font-display leading-tight">
            OpusClip vs Vizard vs Transcriptr
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
            All three tools detect clips with AI. But only one gives you 51+ content tools, workflow automation, and a full multi-track editor — for free.
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-16">
          <div className="bg-card rounded-2xl border-2 border-[#CC3E21]/40 p-6 relative">
            <div className="absolute -top-3 left-4 bg-[#CC3E21] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
              <Crown className="w-3 h-3" /> Best Value
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Transcriptr</h3>
            <p className="text-sm text-white/60 mb-3">All-in-one content platform with AI clip detection, 51+ tools, and workflow automation.</p>
            <p className="text-2xl font-bold text-white">Free<span className="text-sm font-normal text-white/40 ml-1">to start</span></p>
          </div>
          <div className="bg-card rounded-2xl border border-white/[0.06] p-6">
            <h3 className="text-lg font-bold text-white mb-2">OpusClip</h3>
            <p className="text-sm text-white/60 mb-3">Popular AI clipper with virality scoring and basic B-roll. Limited free tier and no content tools.</p>
            <p className="text-2xl font-bold text-white">$19<span className="text-sm font-normal text-white/40 ml-1">/mo</span></p>
          </div>
          <div className="bg-card rounded-2xl border border-white/[0.06] p-6">
            <h3 className="text-lg font-bold text-white mb-2">Vizard AI</h3>
            <p className="text-sm text-white/60 mb-3">Basic AI clipper with captions and auto-reframe. No virality scoring, B-roll, or automation.</p>
            <p className="text-2xl font-bold text-white">$20<span className="text-sm font-normal text-white/40 ml-1">/mo</span></p>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white font-display mb-6">Feature-by-feature comparison</h2>
          <div className="bg-card rounded-2xl border border-white/[0.06] overflow-hidden">
            {/* Desktop */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white/60 bg-muted/20">Feature</th>
                    <th className="py-4 px-6 text-center text-sm font-bold text-white bg-[#CC3E21]/5">Transcriptr</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-white/40">OpusClip</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-white/40">Vizard AI</th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 px-6 text-sm font-medium text-white/70">{row.feature}</td>
                      <td className="py-4 px-6 text-center bg-[#CC3E21]/5">
                        <CellContent value={row.transcriptr} highlight />
                      </td>
                      <td className="py-4 px-6 text-center">
                        <CellContent value={row.opusclip} />
                      </td>
                      <td className="py-4 px-6 text-center">
                        <CellContent value={row.vizard} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="md:hidden p-4 space-y-3">
              {ROWS.map((row, i) => (
                <div key={i} className="bg-white/[0.02] rounded-xl p-4">
                  <p className="text-sm font-medium text-white mb-3">{row.feature}</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[#ff6b4a] font-semibold">Transcriptr</span>
                      <CellContent value={row.transcriptr} highlight />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-white/40">OpusClip</span>
                      <CellContent value={row.opusclip} />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-white/40">Vizard</span>
                      <CellContent value={row.vizard} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white font-display mb-6">The verdict</h2>
          <div className="prose prose-invert prose-zinc max-w-none space-y-4">
            <p className="text-base text-white/60 leading-relaxed">
              Transcriptr wins on <span className="text-white font-semibold">{transcriptrWins} out of {ROWS.length} features</span> compared to both OpusClip and Vizard. While all three tools offer solid AI clip detection, Transcriptr stands apart with its 51+ content tools, workflow automation, and multi-track timeline editor.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              <span className="text-white font-semibold">OpusClip</span> is a strong choice if you only need clip detection with B-roll and captions. But it lacks content repurposing tools and advanced automation.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              <span className="text-white font-semibold">Vizard AI</span> covers the basics of clip detection and captions, but misses virality scoring, B-roll, voiceover, and most advanced features.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              <span className="text-white font-semibold">Transcriptr</span> is the only platform that turns a single video into clips AND 51+ types of content — blog posts, social captions, newsletters, flashcards, and more — with workflow automation to do it all hands-free.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12 bg-card/50 rounded-2xl border border-white/[0.06]">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-display mb-3">
            Try the best AI clip maker — free
          </h2>
          <p className="text-white/60 mb-6">Paste any YouTube URL and see your viral clips in seconds. No credit card required.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/sign-up">
              <Button className="h-12 px-8 bg-[#CC3E21] hover:bg-[#b5351b] text-white font-bold text-base rounded-xl shadow-lg shadow-[#CC3E21]/25 hover:scale-105 transition-all">
                Get Free Clips <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="flex justify-center gap-6 mt-6 text-sm text-white/40">
            <Link href="/compare/opus-clip-alternative" className="hover:text-white/60 transition-colors">
              OpusClip Alternative
            </Link>
            <Link href="/compare/vizard-alternative" className="hover:text-white/60 transition-colors">
              Vizard Alternative
            </Link>
            <Link href="/compare/transcriptr-vs-opusclip" className="hover:text-white/60 transition-colors">
              Transcriptr vs OpusClip
            </Link>
          </div>
        </div>
      </div>
    </main>
  )
}
